import { useState, useEffect, useRef } from "react"
import { api } from "../../lib/api"
import { colors } from "../../lib/colors"
import { downloadCsv, downloadElementAsPng, todayStr } from "../../lib/csv"
import type { AudienceStatsData, DashboardData } from "../../types/analytics"

interface AudienceStatsProps {
  initialData: AudienceStatsData | undefined
  accountIds: number[]
  startDate: string
  endDate: string
}

const countryNames = new Intl.DisplayNames(["ja"], { type: "region" })
const getCountryName = (code: string) => {
  try { return countryNames.of(code) ?? code } catch { return code }
}

const yesterdayStr = () => {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

const AudienceStats = ({ initialData, accountIds, startDate, endDate }: AudienceStatsProps) => {
  const [data, setData]         = useState(initialData)
  const [fetching, setFetching] = useState(false)
  const [date, setDate]         = useState(yesterdayStr)
  const dateInputRef            = useRef<HTMLInputElement>(null)
  const ageGenderRef            = useRef<HTMLDivElement>(null)
  const citiesRef               = useRef<HTMLDivElement>(null)
  const regionsRef              = useRef<HTMLDivElement>(null)

  useEffect(() => { setData(initialData) }, [initialData])

  const handleDateChange = async (newDate: string) => {
    setDate(newDate)
    if (!startDate || !endDate || accountIds.length === 0) return
    setFetching(true)
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate, audience_date: newDate })
      accountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
      const res = await api.get<DashboardData>(`/analytics/dashboard?${params}`)
      setData(res.audience_stats)
    } catch {
      // エラーは無視
    } finally {
      setFetching(false)
    }
  }

  const ageGenderGroups = data?.age_gender_breakdown ?? []
  const regions         = data?.top_regions ?? []
  const cities          = data?.top_cities ?? []

  const maxAgeVal = Math.max(
    ...ageGenderGroups.flatMap(g => [g.male, g.female]),
    1
  )

  const handleAgeGenderCsv = () => downloadCsv(
    [
      ["年齢層", "女性(%)", "男性(%)"],
      ...ageGenderGroups.map(g => [g.range, g.female, g.male]),
    ],
    `audience_age_gender_${todayStr()}.csv`
  )

  const handleCitiesCsv = () => downloadCsv(
    [
      ["都市", "%"],
      ...cities.map(c => [c.city, c.percentage]),
    ],
    `audience_cities_${todayStr()}.csv`
  )

  const handleRegionsCsv = () => downloadCsv(
    [
      ["地域（国）", "%"],
      ...regions.map(r => [getCountryName(r.country) ?? r.country, r.percentage]),
    ],
    `audience_regions_${todayStr()}.csv`
  )

  return (
    <div className={`audience-stats${fetching ? " audience-stats--fetching" : ""}`}>
      <div className="audience-stats__header">
        <span className="audience-stats__title">オーディエンス統計</span>
        <div className="audience-date-picker">
          <button
            className="audience-date-picker__btn"
            onClick={() => dateInputRef.current?.showPicker()}
          >
            <i className="bi bi-calendar3" />
            <span>{date}</span>
          </button>
          <input
            ref={dateInputRef}
            type="date"
            value={date}
            onChange={e => handleDateChange(e.target.value)}
            className="audience-date-picker__input"
          />
        </div>
      </div>

      <div className="audience-stats__grid">
        {/* 年齢層×男女 */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div className="audience-stats__section-title" style={{ marginBottom: 0 }}>年齢層</div>
            {ageGenderGroups.length > 0 && (
              <div className="chart-card__actions">
                <button className="summary-table__csv-btn" onClick={handleAgeGenderCsv}>↓ CSV</button>
                <button className="summary-table__csv-btn" onClick={() => downloadElementAsPng(ageGenderRef.current, `audience_age_gender_${todayStr()}.png`)}>↓ PNG</button>
              </div>
            )}
          </div>
          {ageGenderGroups.length > 0 ? (
            <div ref={ageGenderRef}>
              <div className="age-gender-legend">
                <span className="age-gender-legend__item">
                  <span className="age-gender-legend__dot" style={{ background: colors.chartFemale }} />女性
                </span>
                <span className="age-gender-legend__item">
                  <span className="age-gender-legend__dot" style={{ background: colors.chartMale }} />男性
                </span>
              </div>
              <div className="age-gender-chart">
                {ageGenderGroups.map(({ range, male, female }) => (
                  <div key={range} className="age-gender-row">
                    <span className="age-gender-row__label">{range}</span>
                    <div className="age-gender-row__bars">
                      <div className="age-gender-row__bar-line">
                        <div
                          className="age-gender-row__bar age-gender-row__bar--female"
                          style={{ width: `${(female / maxAgeVal) * 100}%` }}
                        />
                        <span className="age-gender-row__value">{female}%</span>
                      </div>
                      <div className="age-gender-row__bar-line">
                        <div
                          className="age-gender-row__bar age-gender-row__bar--male"
                          style={{ width: `${(male / maxAgeVal) * 100}%` }}
                        />
                        <span className="age-gender-row__value">{male}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: colors.gray400 }}>データなし</p>
          )}
        </div>

        {/* 都市 */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div className="audience-stats__section-title" style={{ marginBottom: 0 }}>都市</div>
            {cities.length > 0 && (
              <div className="chart-card__actions">
                <button className="summary-table__csv-btn" onClick={handleCitiesCsv}>↓ CSV</button>
                <button className="summary-table__csv-btn" onClick={() => downloadElementAsPng(citiesRef.current, `audience_cities_${todayStr()}.png`)}>↓ PNG</button>
              </div>
            )}
          </div>
          {cities.length > 0 ? (
            <div className="region-list" ref={citiesRef}>
              {cities.map(({ city, percentage }, i) => (
                <div key={city} className="region-item">
                  <span className="region-item__rank">{i + 1}</span>
                  <span className="region-item__name">{city}</span>
                  <div className="region-item__bar-wrap">
                    <div className="region-item__bar" style={{ width: `${percentage}%` }} />
                  </div>
                  <span className="region-item__value">{percentage}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 13, color: colors.gray400 }}>データなし</p>
          )}
        </div>

        {/* 地域（国） */}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div className="audience-stats__section-title" style={{ marginBottom: 0 }}>地域（国）</div>
            {regions.length > 0 && (
              <div className="chart-card__actions">
                <button className="summary-table__csv-btn" onClick={handleRegionsCsv}>↓ CSV</button>
                <button className="summary-table__csv-btn" onClick={() => downloadElementAsPng(regionsRef.current, `audience_regions_${todayStr()}.png`)}>↓ PNG</button>
              </div>
            )}
          </div>
          {regions.length > 0 ? (
            <div className="region-list" ref={regionsRef}>
              {regions.map(({ country, percentage }, i) => (
                <div key={country} className="region-item">
                  <span className="region-item__rank">{i + 1}</span>
                  <span className="region-item__name">{getCountryName(country)}</span>
                  <div className="region-item__bar-wrap">
                    <div className="region-item__bar" style={{ width: `${percentage}%` }} />
                  </div>
                  <span className="region-item__value">{percentage}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: 13, color: colors.gray400 }}>データなし</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default AudienceStats
